import type { CouponDetail, GiftVoucherDetail } from '@/src/types/couponTray'
import type { PaxPricingInfo } from '@/src/types/itinerary'
import { useMemo } from 'react'

/**
 * Hook for calculating discount-related values (coupons, gift vouchers, and instant discounts)
 * Processes coupon details, optional gift voucher data (CT), and pax pricing info
 */
const useDiscountCalculation = (
    couponDetails: CouponDetail[] = [],
    paxPricingInfoJson: PaxPricingInfo[] = [],
    fareFamilyEnabledOnReview: boolean = false,
    giftVoucherData: GiftVoucherDetail[] = []
) => {
    // Coupon discount and code are always derived from RTK Query couponDetails
    const { totalCouponAmount, totalCouponAmountWithVat, couponCode } = useMemo(() => {
        let totalCouponAmount = 0
        let totalCouponAmountWithVat = 0
        couponDetails.forEach((couponDetail: CouponDetail) => {
            const { walletPromo = false, amount = 0, ct_vat } = couponDetail || {}
            if (!walletPromo && amount && !isNaN(amount)) {
                totalCouponAmount += amount
                totalCouponAmountWithVat = totalCouponAmountWithVat + amount + (ct_vat || 0)
            }
        })
        return {
            totalCouponAmount,
            totalCouponAmountWithVat,
            couponCode: couponDetails[0]?.couponCode || ''
        }
    }, [couponDetails])

    const totalGvAmount = useMemo(() => {
        if (!Array.isArray(giftVoucherData) || giftVoucherData.length === 0) return 0
        return giftVoucherData
            .filter(v => v?.voucherStatus === 'SUCCESS')
            .reduce((sum, v) => sum + (v.amount ?? 0), 0)
    }, [giftVoucherData])

    // Calculate total instant discount across all pax
    const totalInstantDiscountForRegularFares = useMemo(() => {
        if (!Array.isArray(paxPricingInfoJson) || fareFamilyEnabledOnReview) return 0

        return paxPricingInfoJson.reduce((acc, pax) => {
            const pricingElements = pax.pricingInfoJson?.[0]?.pricingElementJson || []
            const paxDiscount = pricingElements
                .filter(e => e.category === 'DIS')
                .reduce((sum, e) => sum + (e.amount || 0), 0)
            return acc + paxDiscount
        }, 0)
    }, [paxPricingInfoJson, fareFamilyEnabledOnReview])

    return {
        totalCouponAmount,
        totalCouponAmountWithVat,
        couponCode,
        totalGvAmount,
        totalInstantDiscountForRegularFares
    }
}

export default useDiscountCalculation
